import React, { useEffect, useState } from 'react';
import { errorToast, successToast } from '../../../utils/helper';
import Loader from '../../../component/common/Loader';
import SearchInput from '../../../component/common/Input/SearchInput';
import ToggleSwitch from '../../../component/common/ToggleSwitch';
import PrimaryButton from '../../../component/common/Buttons/PrimaryButton';
import { getElloUsersList, assignElloToUsers } from '../../../api/offeringApi';

const PER_PAGE = 10;

const GourmetSocietyAddUsersModal = ({ isOpen, onClose, onSuccess }) => {
  const [loader, setLoader] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchUsers = async (page = 1, append = false) => {
    if (page === 1) setLoader(true);
    else setIsLoadingMore(true);

    try {
      const response = await getElloUsersList({
        page,
        perPage: PER_PAGE,
        search: debouncedSearch || undefined
      });

      const fetched = Array.isArray(response?.data) ? response.data : [];
      const meta = response?.meta || {};

      setUsers((prev) => (append ? [...prev, ...fetched] : fetched));
      setTotalRecords(Number(meta?.totalRecords) || fetched.length);
    } catch (err) {
      errorToast(err?.response?.data?.meta?.message || 'Failed to load users');
    } finally {
      setLoader(false);
      setIsLoadingMore(false);
    }
  };

  // Reset and fetch when modal opens or search changes
  useEffect(() => {
    if (!isOpen) return;
    setSelectedUserIds([]);
    setCurrentPage(1);
    fetchUsers(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, debouncedSearch]);

  const handleToggle = (user) => {
    const userId = String(user.userId || user._id);
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const allIds = users.map((u) => String(u.userId || u._id));
    const allSelected = allIds.every((id) => selectedUserIds.includes(id));
    if (allSelected) {
      setSelectedUserIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedUserIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchUsers(nextPage, true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedUserIds.length === 0) {
      errorToast('Please select at least one user');
      return;
    }

    setLoader(true);
    try {
      const response = await assignElloToUsers(selectedUserIds);
      if (response?.meta?.code === 1 || response?.status === 200) {
        successToast('Users assigned successfully');
        onSuccess?.();
        handleClose();
      } else {
        errorToast(response?.meta?.message || 'Failed to assign users');
      }
    } catch (err) {
      errorToast(err?.response?.data?.meta?.message || 'Failed to assign users');
    } finally {
      setLoader(false);
    }
  };

  const handleClose = () => {
    setUsers([]);
    setSelectedUserIds([]);
    setSearchTerm('');
    setDebouncedSearch('');
    setCurrentPage(1);
    setTotalRecords(0);
    onClose();
  };

  if (!isOpen) return null;

  const hasMore = users.length < totalRecords;
  const allPageSelected =
    users.length > 0 && users.every((u) => selectedUserIds.includes(String(u.userId || u._id)));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-shoorah-darkBorder flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Add Gourmet Society Users
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="px-6 py-4 flex flex-col gap-4 flex-1 overflow-hidden">
            <SearchInput
              id="searchGourmetUsers"
              name="searchGourmetUsers"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
            />

            {users.length > 0 && (
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Users{' '}
                  <span className="text-gray-500 font-normal">
                    ({selectedUserIds.length} selected)
                  </span>
                </p>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-sm text-shoorah-primary hover:text-shoorah-secondary transition"
                >
                  {allPageSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            )}

            <div className="border border-gray-200 dark:border-shoorah-darkBorder rounded-xl overflow-y-auto flex-1">
              {loader ? (
                <div className="flex items-center justify-center py-12">
                  <Loader />
                </div>
              ) : users.length > 0 ? (
                <>
                  {users.map((user) => {
                    const userId = String(user.userId || user._id);
                    const isSelected = selectedUserIds.includes(userId);
                    return (
                      <div
                        key={userId}
                        className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100 dark:border-shoorah-darkBorder last:border-b-0"
                      >
                        <div className="flex-1 min-w-0 mr-4">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {user.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {user.email}
                          </p>
                        </div>
                        <ToggleSwitch
                          toggleValue={isSelected}
                          setToggleValue={() => handleToggle(user)}
                          disabled={false}
                        />
                      </div>
                    );
                  })}

                  {hasMore && (
                    <div className="flex justify-center py-3">
                      {isLoadingMore ? (
                        <span className="text-sm text-gray-500 dark:text-gray-400">Loading…</span>
                      ) : (
                        <button
                          type="button"
                          onClick={handleLoadMore}
                          className="text-sm text-shoorah-primary hover:text-shoorah-secondary transition"
                        >
                          Load more
                        </button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                  <p className="text-sm">No users found</p>
                  {searchTerm && <p className="text-xs mt-1">Try a different search term</p>}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-shoorah-darkBorder flex gap-3">
            <PrimaryButton btnText="Cancel" onClick={handleClose} btnType="button" className="flex-1" />
            <PrimaryButton
              btnText={
                loader
                  ? 'Adding…'
                  : `Add Users${selectedUserIds.length > 0 ? ` (${selectedUserIds.length})` : ''}`
              }
              btnType="submit"
              disabled={loader || selectedUserIds.length === 0}
              className="flex-1"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default GourmetSocietyAddUsersModal;
